/**
 * Activity — polls GET /api/fleet/activity every 10s.
 * Shows a chronological list of fleet events with type badge, summary, time, actor.
 * delegation_failure events are coloured red to stand out.
 *
 * The api client handles silent token refresh transparently. An UnauthorizedError
 * reaching this component means the console session is truly gone.
 */
import { useEffect, useState, useCallback } from 'react'
import { api, UnauthorizedError } from '../api/client'
import type { ActivityEvent } from '../api/client'
import { relativeTime } from '../utils/relativeTime'

const POLL_INTERVAL_MS = 10_000
const DEFAULT_LIMIT = 50

interface TypeMeta {
  label: string
  badgeClass: string
  rowClass: string
}

function typeMeta(type: string): TypeMeta {
  switch (type) {
    case 'approval_created':
      return { label: 'Approval', badgeClass: 'bg-blue-100 text-blue-800', rowClass: '' }
    case 'approval_responded':
      return { label: 'Responded', badgeClass: 'bg-green-100 text-green-800', rowClass: '' }
    case 'gate_raised':
      return { label: 'Gate raised', badgeClass: 'bg-teal-100 text-teal-800', rowClass: '' }
    case 'gate_answered':
      return { label: 'Gate response', badgeClass: 'bg-teal-100 text-teal-800', rowClass: '' }
    case 'delegation_failure':
      return { label: 'Failure', badgeClass: 'bg-red-100 text-red-800', rowClass: 'bg-red-50' }
    case 'grade':
      return { label: 'Grade', badgeClass: 'bg-purple-100 text-purple-800', rowClass: '' }
    case 'self_edit':
      return { label: 'Self-edit', badgeClass: 'bg-amber-100 text-amber-800', rowClass: '' }
    case 'experiment':
      return { label: 'Experiment', badgeClass: 'bg-indigo-100 text-indigo-800', rowClass: '' }
    default:
      return { label: type, badgeClass: 'bg-gray-100 text-gray-600', rowClass: '' }
  }
}

function EventRow({ event }: { event: ActivityEvent }) {
  const meta = typeMeta(event.type)
  return (
    <li className={`px-5 py-3 flex items-start gap-4 ${meta.rowClass}`}>
      <span className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium shrink-0 ${meta.badgeClass}`}>
        {meta.label}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-800">{event.summary || '—'}</p>
        {event.ref && (
          <p className="text-xs text-gray-400 mt-0.5 truncate font-mono">{event.ref}</p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs text-gray-500">{relativeTime(event.ts)}</p>
        <p className="text-xs text-gray-400">{event.actor || '—'}</p>
      </div>
    </li>
  )
}

export function Activity() {
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [secsAgo, setSecsAgo] = useState(0)

  const fetchActivity = useCallback(async () => {
    try {
      const res = await api.getActivity(DEFAULT_LIMIT)
      setEvents(res.events)
      setLastUpdated(new Date())
      setSecsAgo(0)
      setError(null)
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        setError('Session expired. Please re-authenticate.')
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load activity.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchActivity()
    const interval = setInterval(() => { void fetchActivity() }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchActivity])

  useEffect(() => {
    if (!lastUpdated) return
    const t = setInterval(() => {
      setSecsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000))
    }, 1000)
    return () => clearInterval(t)
  }, [lastUpdated])

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Activity</h1>
          <p className="text-sm text-gray-500 mt-1">Recent fleet events (last {DEFAULT_LIMIT}).</p>
        </div>
        {lastUpdated && (
          <p className="text-xs text-gray-400">Updated {secsAgo}s ago</p>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !error && (
        <div className="text-sm text-gray-400 animate-pulse">Loading activity...</div>
      )}

      {!loading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {events.length === 0 ? (
            <p className="px-5 py-10 text-sm text-gray-400 text-center">No activity yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {events.map(ev => (
                <EventRow key={ev.id} event={ev} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
